
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { apiRequest } from '@/lib/api-client';
import { Link } from 'react-router-dom';
import { formatInAlbania } from '@/lib/date-utils';
import { cn } from '@/lib/utils';

const COLLAPSED_COUNT = 5;

interface AttendanceProps {
  selectedGym: string;
  className?: string;
}

export function Attendance({ selectedGym, className }: AttendanceProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym,
  });

  // Filter for active check-ins (checkOut is null)
  const activeCheckIns =
    data?.attandanceCount?.filter((checkin) => checkin.checkOut === null) || [];

  const hasMore = activeCheckIns.length > COLLAPSED_COUNT;
  const visibleCheckIns = expanded
    ? activeCheckIns
    : activeCheckIns.slice(0, COLLAPSED_COUNT);

  const handleCheckOut = async (checkInId: string, userId: string) => {
    if (!selectedGym) {
      toast({
        title: 'Error',
        description: 'No gym selected',
        variant: 'destructive',
      });
      return;
    }

    setCheckingOut(checkInId);

    try {
      await apiRequest({
        method: 'POST',
        url: '/webhook/manual',
        data: {
          gymId: selectedGym,
          userId: userId,
          type: 'out',
        },
      });

      // Refresh dashboard data with the correct query key
      await queryClient.invalidateQueries({
        queryKey: ['dashboardData', selectedGym],
      });

      toast({
        title: 'Success',
        description: t('checkIn.checkOutSuccess'),
      });
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Error',
        description: t('checkIn.checkOutError'),
        variant: 'destructive',
      });
    } finally {
      setCheckingOut(null);
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('dashboard.attendance')}</CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to="/check-in">{t('common.viewAll')}</Link>
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[200px]" />
                  <Skeleton className="h-4 w-[160px]" />
                </div>
              </div>
            ))}
          </div>
        ) : activeCheckIns.length > 0 ? (
          <>
            <div
              className={cn(
                'divide-y divide-border',
                expanded && 'max-h-[340px] overflow-y-auto pr-1'
              )}
            >
            {visibleCheckIns.map((checkin) => (
              <div
                key={checkin._id}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                    {checkin?.userId?.name?.charAt(0)}
                    {checkin?.userId?.surname?.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {checkin?.userId?.name} {checkin?.userId?.surname}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {formatInAlbania(checkin.checkIn, 'DD-MM-YYYY HH:mm')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400"
                  >
                    {t('checkIn.active')}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleCheckOut(checkin._id, checkin.userId._id)
                    }
                    disabled={checkingOut === checkin._id}
                  >
                    {checkingOut === checkin._id
                      ? t('checkIn.processing')
                      : t('checkIn.checkOut')}
                  </Button>
                </div>
              </div>
            ))}
            </div>
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 w-full text-muted-foreground hover:text-foreground"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded
                  ? t('common.showLess', 'Show less')
                  : t('common.showMoreCount', {
                      count: activeCheckIns.length - COLLAPSED_COUNT,
                      defaultValue: `Show ${activeCheckIns.length - COLLAPSED_COUNT} more`,
                    })}
              </Button>
            )}
          </>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t('checkIn.noCheckIns')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
